import * as React from 'react';
import { Dialog, Classes, Button, FileInput, FormGroup, NumericInput } from '@blueprintjs/core';
import { Form, FormField, FormFieldType } from '@patterns/form';
import { Product } from '../../models/product';
import { deepEqual, convertFileToBase64 } from '@patterns/core';
import { ProductRepository } from '../../repository';
import CategorySelect from '../category_select';
import produce from 'immer';
import { FlexColumn, SaveButton, ConfirmButton, FlexRow } from '@patterns/ui';
import { session } from '../../session'
import { withTranslation, WithTranslation } from 'react-i18next';

const ProductForm = Form.ofType<Product>();

export interface Props extends WithTranslation {
  isOpen: boolean  
  onClose: (changed: boolean) => void
  onDelete: (device: Product) => void
  product: Product
}

export interface State {
  attachment: string
  isUploading: boolean
  product: Product
  stringValue: string
}

export class ProductsDialog extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props)
    this.state = {
      attachment: '',
      isUploading: false,
      product: props.product,
      stringValue: props.product.price.toFixed(2).replace('.', ',')
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!deepEqual(this.props.product, prevProps.product)) {
      this.setState({
        attachment: '',
        product: this.props.product,
        stringValue: this.props.product.price.toFixed(2).replace('.', ',')
      })
    }
  }

  private getFields = () => {
    const { t } = this.props;
    return [
      {
        id: 'name',
        label: t('name'),
        type: FormFieldType.Text
      },
      {
        id: 'sku',
        label: t('fleet.sku'),
        type: FormFieldType.Text
      },
      {
        id: 'price',
        label: t('price'),
        type: FormFieldType.Custom,
        customElement: (product, onChange) => <NumericInput 
          value={this.state.stringValue}
          locale="fi"
          fill
          buttonPosition='none'
          onValueChange={(value, stringValue) => {
            this.setState({
              stringValue
            }, () => {
              product.price = value;
              onChange(product)
            })
          }}
        />
      },
      {
        id: 'category',
        label: t('category'),
        type: FormFieldType.Custom,
        customElement: (device, onChange) => {
          return <CategorySelect 
            category={device.category}
            onChange={(category) => {
              const newDevice = produce(device, newDevice => {
                if (category) {
                  newDevice.category = category;
                }
              });
              onChange(newDevice)
            }}
          />
        }
      },
      {
        id: 'description',
        label: t('description'),
        type: FormFieldType.TextArea
      },
    ] as FormField<Product>[]
  }

  private delete = async () => this.props.onDelete(this.state.product)

  private onChange = (device: Product) => this.setState(produce(this.state, state => {
    state.product = device
  }))

  private onClose = () => this.props.onClose(false)

  private save = async () => {
    await ProductRepository.save(this.state.product);
    this.props.onClose(true)
  }

  public render() {
    const { t } = this.props;
    return (
      <Dialog canOutsideClickClose={false} title={t('product')} isOpen={this.props.isOpen} onClose={this.onClose} className="bp3-dark">
        <div className={Classes.DIALOG_BODY}>
          <ProductForm 
            inline={true}
            fill={true}
            item={this.state.product}
            fields={this.getFields()}
            onChange={this.onChange}
          />

          <FlexColumn className="patterns-form">
            <FormGroup 
              label={t('image')}
              inline={true}
              className="patterns-form-field-group patterns-form-field-group-fill">
              
              <FileInput
                fill={true}
                className="attachment-input m-b-12"
                text={this.state.product.image ? t('image') : t('add_image')}
                inputProps={{
                  accept: '.jpg,.jpeg,.png',
                  multiple: false
                }}
                buttonText={t('browse')}
                hasSelection={this.state.product.image.length > 0}
                onInputChange={async value => {
                  if (value.currentTarget.files) {
                    const file = value.currentTarget.files[0];
                    const data = await convertFileToBase64(file);
                    const newDevice = produce(this.state.product, newDevice => {
                      newDevice.image = data;
                    })
                    this.setState({ product: newDevice })
                  }
                }}
              />
            </FormGroup>

            { this.state.product.exists && <FormGroup 
              label="   " 
              inline={true}
              className="patterns-form-field-group patterns-form-field-group-fill">
              
              <img 
                className="form-image-preview" 
                src={`/api/products/${this.state.product.id}/image?token=${session.token}`} 
                alt=""
              />
            </FormGroup> }
          </FlexColumn>

        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <FlexRow flex={1}>
              <ConfirmButton
                title={t('delete')}
                confirmTitle={t('confirm')}
                onConfirm={this.delete}
              />
            </FlexRow>
            <Button
              text={t('cancel')}
              onClick={this.onClose}
            />
            <SaveButton 
              onClick={this.save}
            />
          </div>
        </div>
      </Dialog>
    );
  }
}

export default withTranslation()(ProductsDialog)